/* Ganda Hair · gh-motion.js
   Shared scroll / 3D engine for the Golden Hour cinematic layer.
   No external libraries. Transform and opacity only. ~6KB min.
   Spec: .brand-studio-work/guidelines.md section 8 (Motion System).
   Modules (bound by data attributes):
     [data-gh-film]      pinned scroll film (scene steps + before/after wipe)
     [data-gh-scroll-film] one continuous scroll-scrubbed video (0..duration, no autoplay)
     [data-gh-wipe]      wipe step inside a film (divider 8% to 92%)
     [data-gh-float]     3D floating product (idle Y rotation + sine float + scroll link, 0.08 lerp)
     [data-gh-fan]       shade fan opening in 3D on scroll
     [data-gh-parallax]  subtle parallax (value = total % displacement, max 12, off below 768px)
     [data-gh-tilt]      gentle 3D card tilt on pointer (fine pointers, >=990px only)
     [data-gh-sweep]     gold light sweep, fires once per section entry
     [data-gh-reveal]    reveal on scroll, fires once, never re-animates
   prefers-reduced-motion: everything static (html.gh-static), sections carry the static CSS. */
(function () {
  'use strict';
  var doc = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  doc.classList.add('gh-js');

  /* ---------- mobile catalog carousel + shade-to-checkout bridge ----------
     Native horizontal scrolling does the gesture work. We write transform,
     depth, and opacity for the 3D cylinder illusion, and keep the commerce bridge
     usable even when reduced motion is enabled. */
  var catalogShadeMap = {
    'the-set-curly': ['Black', 'Dark Brown', 'Brown', 'Reddish'],
    'the-set-straight': ['Black', 'Dark Brown'],
    'clip-in-bangs': ['Black', 'Dark Brown', 'Light Brown', 'Brown Black'],
    'the-hair-bun': ['Black', 'Brown', 'Dark Brown'],
    'the-full-look-set': ['Black', 'Dark Brown'],
    'the-duo-set': ['Black', 'Dark Brown']
  };
  var catalogProductLabel = {
    'the-set-curly': 'The Set · Curly',
    'the-set-straight': 'The Set · Straight',
    'clip-in-bangs': 'Clip-In Bangs',
    'the-hair-bun': 'The Hair Bun',
    'the-full-look-set': 'The Full Look Set',
    'the-duo-set': 'The Duo Set'
  };
  var catalogRouteMap = {
    'the-set-curly': { 'Black': 'GH-CURLY-BLK → Gladking / Code 2 Black', 'Dark Brown': 'GH-CURLY-DBR → Gladking / Code 4 Dark brown', 'Brown': 'GH-CURLY-BRN → Gladking / Code 230 brown', 'Reddish': 'GH-CURLY-RED → Gladking / Code 233 Reddish' },
    'the-set-straight': { 'Black': { '60cm': 'GH-STR-BLK-60 → WIGSIN / 60CM x color 2', '80cm': 'GH-STR-BLK-80 → WIGSIN / 80CM x color 2' }, 'Dark Brown': { '60cm': 'GH-STR-DBR-60 → WIGSIN / 60CM x color 4', '80cm': 'GH-STR-DBR-80 → WIGSIN / 80CM x color 4' } },
    'clip-in-bangs': { 'Black': 'GH-BANGS-NBLK → O-New / Natural Black', 'Dark Brown': 'GH-BANGS-DBR → O-New / Dark Brown', 'Light Brown': 'GH-BANGS-LBR → O-New / Light Brown', 'Brown Black': 'GH-BANGS-BBL → O-New / Brown Black' },
    'the-hair-bun': { 'Black': 'GH-BUN-BLK → FineOne / Black', 'Brown': 'GH-BUN-BRN → FineOne / Brown', 'Dark Brown': 'GH-BUN-DBR → UNLIBENTA / Dark Brown' },
    'the-full-look-set': { 'Black': 'GH-FULL-BLK → Gladking + O-New + FineOne', 'Dark Brown': 'GH-FULL-DBR → Gladking + O-New + UNLIBENTA' },
    'the-duo-set': { 'Black': 'GH-DUO-BLK → component routing + Second shade', 'Dark Brown': 'GH-DUO-DBR → component routing + Second shade' }
  };
  var catalogLengthMap = {
    'the-set-straight': ['60cm', '80cm']
  };
  function initCommerceBridge() {
    var checkout = document.querySelector('[data-gh-checkout]');
    if (!checkout) { return; }
    var select = checkout.querySelector('[data-gh-product-select]');
    var shadeButtons = [].slice.call(checkout.querySelectorAll('[data-gh-shade-choice]'));
    var pay = checkout.querySelector('[data-gh-checkout-link]');
    var route = checkout.querySelector('[data-gh-checkout-route]');
    var form = checkout.querySelector('[data-gh-checkout-form]');
    var variantInput = checkout.querySelector('[data-gh-checkout-variant]');
    var submit = checkout.querySelector('[data-gh-checkout-submit]');
    var lengthWrap = checkout.querySelector('[data-gh-length-wrap]');
    var lengthSelect = checkout.querySelector('[data-gh-length-select]');
    var routeNodes = [].slice.call(checkout.querySelectorAll('[data-gh-route]'));
    var product = select && select.value ? select.value : 'the-set-curly';
    var shade = 'Black';
    var length = '60cm';
    function render() {
      var allowed = catalogShadeMap[product] || ['Black'];
      if (allowed.indexOf(shade) === -1) { shade = allowed[0]; }
      var lengths = catalogLengthMap[product] || [];
      if (lengths.indexOf(length) === -1) { length = lengths[0] || ''; }
      shadeButtons.forEach(function (button) {
        var value = button.getAttribute('data-gh-shade-choice');
        var enabled = allowed.indexOf(value) > -1;
        button.disabled = !enabled;
        button.setAttribute('aria-pressed', String(enabled && value === shade));
      });
      if (select) { select.value = product; }
      if (lengthWrap) { lengthWrap.hidden = lengths.length === 0; }
      if (lengthSelect) {
        lengthSelect.disabled = lengths.length === 0;
        if (lengths.length) { lengthSelect.value = length; }
      }
      var routeConfig = catalogRouteMap[product] && catalogRouteMap[product][shade];
      var routeLabel = (routeConfig && typeof routeConfig === 'object' ? routeConfig[length] : routeConfig) || 'Supplier route requires confirmation before ordering';
      var selectedOption = select && select.options[select.selectedIndex];
      var routeNode = routeNodes.find(function (node) {
        var routeShade = node.getAttribute('data-gh-route-shade');
        var routeAlias = node.getAttribute('data-gh-route-alias');
        var matchesShade = routeShade === shade || routeAlias === shade;
        var matchesLength = lengths.length === 0 || node.getAttribute('data-gh-route-length') === length;
        return node.getAttribute('data-gh-route-product') === product && matchesShade && matchesLength && node.getAttribute('data-gh-route-available') !== 'false';
      });
      var productUrl = (routeNode && routeNode.getAttribute('data-gh-route-url')) || (selectedOption && selectedOption.getAttribute('data-product-url')) || '';
      var variantId = routeNode && routeNode.getAttribute('data-gh-route-variant');
      if (variantInput) { variantInput.value = variantId || ''; }
      if (submit) { submit.disabled = !variantId; }
      if (pay) { pay.href = productUrl ? (productUrl + (variantId ? '?variant=' + encodeURIComponent(variantId) : '?shade=' + encodeURIComponent(shade))) : 'checkout?product=' + encodeURIComponent(product) + '&shade=' + encodeURIComponent(shade); }
      if (route) { route.textContent = 'Routing preview: ' + (catalogProductLabel[product] || product) + ' · ' + shade + (lengths.length ? ' · ' + length : '') + ' → ' + routeLabel; }
    }
    function selectProduct(next) { if (catalogShadeMap[next]) { product = next; render(); } }
    function selectShade(next) { var allowed = catalogShadeMap[product] || []; if (allowed.indexOf(next) > -1) { shade = next; render(); } }
    function selectLength(next) { var allowed = catalogLengthMap[product] || []; if (allowed.indexOf(next) > -1) { length = next; render(); } }
    if (select) { select.addEventListener('change', function () { selectProduct(select.value); }); }
    if (lengthSelect) { lengthSelect.addEventListener('change', function () { selectLength(lengthSelect.value); }); }
    shadeButtons.forEach(function (button) { button.addEventListener('click', function () { selectShade(button.getAttribute('data-gh-shade-choice')); }); });
    if (form) { form.addEventListener('submit', function (event) { if (!variantInput || !variantInput.value) { event.preventDefault(); if (route) { route.textContent = 'This shade is not launch-ready yet. Choose a mapped product variant or contact us for a shade match.'; } } }); }
    [].slice.call(document.querySelectorAll('[data-gh-photo-product]')).forEach(function (card) {
      var activate = function (event) {
        if (event && event.preventDefault) { event.preventDefault(); }
        selectProduct(card.getAttribute('data-gh-photo-product'));
        selectShade(card.getAttribute('data-gh-photo-shade'));
        checkout.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      };
      card.addEventListener('click', activate);
      card.addEventListener('keydown', function (event) { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(); } });
    });
    [].slice.call(document.querySelectorAll('[data-gh-select-product]')).forEach(function (button) {
      button.addEventListener('click', function () { selectProduct(button.getAttribute('data-gh-select-product')); });
    });
    [].slice.call(document.querySelectorAll('[data-gh-shade]')).forEach(function (card) {
      var activate = function () {
        var value = card.getAttribute('data-gh-shade');
        [].slice.call(document.querySelectorAll('[data-gh-shade]')).forEach(function (item) { item.classList.toggle('is-selected', item === card); });
        if (value === 'Brown Black' || value === 'Light Brown') { selectProduct('clip-in-bangs'); }
        else if (value === 'Reddish') { selectProduct('the-set-curly'); }
        selectShade(value);
        checkout.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      };
      card.addEventListener('click', activate);
      card.addEventListener('keydown', function (event) { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(); } });
    });
    render();
  }
  function initCatalogCarousels() {
    [].slice.call(document.querySelectorAll('[data-gh-carousel]')).forEach(function (carousel) {
      var cards = [].slice.call(carousel.querySelectorAll('[data-gh-carousel-card]'));
      var frame = null;
      function update() {
        frame = null;
        if (reduced) {
          cards.forEach(function (card) { card.style.transform = ''; card.style.opacity = ''; });
          return;
        }
        var box = carousel.getBoundingClientRect();
        var center = box.left + box.width / 2;
        cards.forEach(function (card) {
          var cardBox = card.getBoundingClientRect();
          var distance = (cardBox.left + cardBox.width / 2 - center) / (cardBox.width * 1.05);
          var clamped = Math.max(-1.25, Math.min(1.25, distance));
          var angle = (clamped * -16).toFixed(2);
          var scale = (1 - Math.min(Math.abs(clamped), 1) * .065).toFixed(3);
          var opacity = (1 - Math.min(Math.abs(clamped), 1.15) * .22).toFixed(3);
          var depth = ((1 - Math.min(Math.abs(clamped), 1)) * 34).toFixed(1);
          card.style.transform = 'translate3d(0,0,' + depth + 'px) rotateY(' + angle + 'deg) scale(' + scale + ')';
          card.style.opacity = opacity;
        });
      }
      function schedule() { if (frame === null) { frame = requestAnimationFrame(update); } }
      carousel.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      var previous = carousel.parentElement.querySelector('[data-gh-carousel-prev]');
      var next = carousel.parentElement.querySelector('[data-gh-carousel-next]');
      var move = function (direction) {
        var width = cards[0] ? cards[0].getBoundingClientRect().width + 14 : carousel.clientWidth * .76;
        carousel.scrollBy({ left: direction * width, behavior: reduced ? 'auto' : 'smooth' });
      };
      if (previous) { previous.addEventListener('click', function () { move(-1); }); }
      if (next) { next.addEventListener('click', function () { move(1); }); }
      schedule();
    });
  }
  initCommerceBridge();
  initCatalogCarousels();
  if (reduced) { doc.classList.add('gh-static'); return; }

  var vh = window.innerHeight;
  var isMobile = window.innerWidth < 768;
  var EASE_POW = function (t) { return 1 - Math.pow(1 - t, 3); }; /* easeOutCubic ≈ cubic-bezier(0.33, 1, 0.68, 1) — scroll-progress easing, NOT the brand UI token */
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  /* ---------- reveal (once) ---------- */
  var reveals = [].slice.call(document.querySelectorAll('[data-gh-reveal]'));
  if (reveals.length && 'IntersectionObserver' in window) {
    var ro = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); ro.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -15% 0px' });
    reveals.forEach(function (el) { ro.observe(el); });
  } else {
    reveals.forEach(function (el) { el.classList.add('is-in'); });
  }

  /* ---------- gold sweep (once per section entry, max once per viewport) ---------- */
  var sweeps = [].slice.call(document.querySelectorAll('[data-gh-sweep]'));
  if (sweeps.length && 'IntersectionObserver' in window) {
    var so = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('gh-sweep-run'); so.unobserve(e.target); }
      });
    }, { threshold: 0.5 });
    sweeps.forEach(function (el) { so.observe(el); });
  }

  /* ---------- scroll-driven registry (film, fan, parallax, float scroll link) ---------- */
  var films = [].slice.call(document.querySelectorAll('[data-gh-film]')).map(function (track) {
    var steps = [].slice.call(track.querySelectorAll('[data-gh-scene-step]'));
    var stage = track.querySelector('.gh-film__stage') || track;
    return {
      track: track, stage: stage, steps: steps,
      wipeIdx: steps.findIndex(function (s) { return s.hasAttribute('data-gh-wipe'); }),
      wipeAfter: stage.querySelector('.gh-film__pane--after'),
      wipeDivider: stage.querySelector('.gh-film__divider')
    };
  });
  var fans = [].slice.call(document.querySelectorAll('[data-gh-fan]')).map(function (el) {
    return { el: el, cards: [].slice.call(el.children), spread: parseFloat(el.getAttribute('data-gh-fan')) || 16 };
  });
  var paras = isMobile ? [] : [].slice.call(document.querySelectorAll('[data-gh-parallax]')).map(function (el) {
    return { el: el, amt: clamp(parseFloat(el.getAttribute('data-gh-parallax')) || -8, -12, 12) };
  });
  var floats = [].slice.call(document.querySelectorAll('[data-gh-float]')).map(function (el) {
    return { el: el, y: 0, ty: 0, x: 0, tx: 0, visible: true };
  });
  /* One video is the entire scroll experience. There are no scene windows,
     crossfades, slide images, or autoplay transitions: p=0 is frame 0 and
     p=1 is the last frame of the supplied MP4. */
  var scrollFilms = [].slice.call(document.querySelectorAll('[data-gh-scroll-film]')).map(function (track) {
    return {
      track: track,
      video: track.querySelector('[data-gh-scroll-video]'),
      duration: 0,
      target: 0,
      seeking: false,
      seekTimer: null,
      seekFrame: null
    };
  });
  function requestScrollFilmSeek(f) {
    if (!f.video || f.seekFrame !== null) { return; }
    f.seekFrame = requestAnimationFrame(function () {
      f.seekFrame = null;
      seekScrollFilm(f);
    });
  }
  function seekScrollFilm(f) {
    var video = f.video;
    if (!video || f.seeking || video.readyState < 1 || !isFinite(video.duration) || video.duration <= 0) { return; }
    f.duration = video.duration;
    var target = f.target * f.duration;
    /* Coalesce rapid wheel/touch updates into one decoder request. Replacing
       a seek on every scroll event makes Chromium repeatedly throw away the
       frame it is decoding, which is the visible stutter this film had. */
    if (Math.abs(video.currentTime - target) <= 0.002) { return; }
    f.seeking = true;
    if (!video.paused) { video.pause(); }
    try {
      video.currentTime = target;
      f.seekTimer = window.setTimeout(function () {
        f.seeking = false;
        f.seekTimer = null;
        requestScrollFilmSeek(f);
      }, 280);
    } catch (error) {
      f.seeking = false;
    }
  }
  function syncScrollFilm(f, p) {
    if (!f.video) { return; }
    f.target = clamp(p, 0, 1);
    if (f.video.readyState < 1 || !isFinite(f.video.duration) || f.video.duration <= 0) { return; }
    f.duration = f.video.duration;
    requestScrollFilmSeek(f);
  }
  scrollFilms.forEach(function (f) {
    if (!f.video) { return; }
    f.video.autoplay = false;
    f.video.loop = false;
    f.video.muted = true;
    f.video.preload = 'auto';
    f.video.pause();
    var readDuration = function () {
      f.duration = isFinite(f.video.duration) ? f.video.duration : 0;
      syncScrollFilm(f, f.target);
    };
    f.video.addEventListener('seeked', function () {
      if (f.seekTimer !== null) {
        window.clearTimeout(f.seekTimer);
        f.seekTimer = null;
      }
      f.seeking = false;
      requestScrollFilmSeek(f);
    });
    f.video.addEventListener('error', function () {
      if (f.seekTimer !== null) {
        window.clearTimeout(f.seekTimer);
        f.seekTimer = null;
      }
      f.seeking = false;
    });
    f.video.addEventListener('loadedmetadata', readDuration);
    f.video.addEventListener('durationchange', readDuration);
    if (f.video.readyState >= 1) { readDuration(); } else { f.video.load(); }
  });
  if (floats.length && 'IntersectionObserver' in window) {
    var fo = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var f = floats.filter(function (x) { return x.el === e.target; })[0];
        if (f) { f.visible = e.isIntersecting; }
      });
      ensureLoop();
    }, { rootMargin: '50% 0px' });
    floats.forEach(function (f) { fo.observe(f.el); });
  }

  function trackProgress(track) {
    var r = track.getBoundingClientRect();
    var span = r.height - vh;
    if (span <= 0) { return r.top < vh * 0.5 ? 1 : 0; }
    return clamp(-r.top / span, 0, 1);
  }

  function updateFilm(f, p) { /* write-only: p measured in the read phase */
    var n = f.steps.length;
    if (!n) { return; }
    var seg = Math.min(Math.floor(p * n), n - 1);
    f.steps.forEach(function (s, i) {
      var active = i === seg;
      if (active !== s.classList.contains('is-active')) {
        s.classList.toggle('is-active', active);
        s.style.willChange = active ? 'opacity, transform' : '';
      }
    });
    if (f.wipeIdx > -1) {
      var local = clamp(p * n - f.wipeIdx, 0, 1);
      var pos = (8 + EASE_POW(local) * 84).toFixed(2) + '%'; /* 8% to 92%, never the edges */
      /* direct writes reproduce the old CSS var exactly:
         clip-path: inset(0 0 0 var(--gh-wipe)) on the after pane, left: var(--gh-wipe) on the divider */
      if (f.wipeAfter) { f.wipeAfter.style.clipPath = 'inset(0 0 0 ' + pos + ')'; }
      if (f.wipeDivider) { f.wipeDivider.style.left = pos; }
    }
  }

  function updateScrollFilm(f, p) { /* write-only: p measured in the read phase */
    syncScrollFilm(f, p);
  }

  function updateFan(f, p) { /* write-only: p measured in the read phase */
    var n = f.cards.length;
    f.cards.forEach(function (card, i) {
      var k = i - (n - 1) / 2;
      var rot = k * f.spread * p;
      var tx = k * 6 * p;   /* % sideways drift as the fan opens */
      var ty = Math.abs(k) * 2 * p; /* outer blades sit slightly lower, like a real fan */
      card.style.transform = 'translate3d(' + tx + '%,' + ty + '%,0) rotate(' + rot + 'deg)';
    });
    f.el.classList.toggle('is-open', p > 0.98);
  }

  function updateParallax(item, p) { /* write-only: p measured in the read phase */
    item.el.style.transform = 'translate3d(0,' + ((p - 0.5) * item.amt).toFixed(3) + '%,0)';
  }

  var ticking = false;
  function onScroll() {
    if (ticking) { return; }
    ticking = true;
    requestAnimationFrame(function () {
      /* phase 1 — reads only (no style writes before the last rect read) */
      var filmData = films.map(function (f) { return { f: f, p: trackProgress(f.track) }; });
      var scrollFilmData = scrollFilms.map(function (f) { return { f: f, p: trackProgress(f.track) }; });
      var fanData = fans.map(function (f) {
        var r = f.el.getBoundingClientRect();
        return { f: f, p: EASE_POW(clamp((vh * 0.92 - r.top) / (vh * 0.55), 0, 1)) };
      });
      var paraData = paras.map(function (item) {
        var scene = item.el.closest('[data-gh-scene]') || item.el.parentElement;
        var r = scene.getBoundingClientRect();
        return { item: item, p: clamp((vh - r.top) / (vh + r.height), 0, 1) };
      });
      /* phase 2 — writes only */
      filmData.forEach(function (d) { updateFilm(d.f, d.p); });
      scrollFilmData.forEach(function (d) { updateScrollFilm(d.f, d.p); });
      fanData.forEach(function (d) { updateFan(d.f, d.p); });
      paraData.forEach(function (d) { updateParallax(d.item, d.p); });
      ticking = false;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () {
    vh = window.innerHeight;
    isMobile = window.innerWidth < 768;
    onScroll();
  });
  onScroll();

  /* ---------- 3D float: idle rotation + scroll link damped with 0.08 lerp.
     The sine bob lives in CSS (.gh-float__idle / gh-idle-bob keyframes).
     rAF loop runs only while a float is visible; on stop, rotation freezes (acceptable). ---------- */
  var rafId = null;
  var t0 = performance.now();
  function loop(now) {
    if (!floats.some(function (f) { return f.visible; })) { rafId = null; return; }
    var t = (now - t0) / 1000;
    floats.forEach(function (f) {
      if (!f.visible) { return; }
      var scene = f.el.closest('[data-gh-film], [data-gh-crossfilm]');
      var p = scene ? trackProgress(scene) : 0.5;
      f.ty = -45 + p * 90;       /* -45deg to +45deg on Y across the pin */
      f.tx = p * -8;             /* 0 to -8deg on X */
      f.y += (f.ty - f.y) * 0.08;
      f.x += (f.tx - f.x) * 0.08;
      var idle = (t * 14.9) % 360;              /* 0.26 rad/s */
      f.el.style.transform = 'rotateY(' + (idle + f.y).toFixed(2) + 'deg) rotateX(' + f.x.toFixed(2) + 'deg)';
    });
    rafId = requestAnimationFrame(loop);
  }
  function ensureLoop() {
    if (rafId === null && floats.some(function (f) { return f.visible; })) {
      rafId = requestAnimationFrame(loop);
    }
  }
  if (floats.length) { ensureLoop(); }

  /* ---------- gentle 3D tilt (cards) ---------- */
  if (window.matchMedia('(pointer: fine)').matches && window.innerWidth >= 990) {
    [].slice.call(document.querySelectorAll('[data-gh-tilt]')).forEach(function (card) {
      var MAX = 4; /* deg */
      card.addEventListener('pointerenter', function () {
        card.style.transition = 'transform 0s';
      });
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var rx = ((e.clientY - r.top) / r.height - 0.5) * -2 * MAX;
        var ry = ((e.clientX - r.left) / r.width - 0.5) * 2 * MAX;
        card.style.transform = 'translateY(-4px) perspective(700px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
      });
      card.addEventListener('pointerleave', function () {
        card.style.transition = 'transform 260ms cubic-bezier(0.23, 1, 0.32, 1)';
        card.style.transform = '';
        card.addEventListener('transitionend', function onEnd(e) {
          if (e.propertyName !== 'transform') { return; }
          card.removeEventListener('transitionend', onEnd);
          card.style.transition = '';
        });
      });
    });
  }
})();
